import {
    Children,
    cloneElement,
    ComponentType,
    forwardRef,
    Ref,
    useCallback,
    useEffect,
    useRef,
    useState,
} from 'react';
import { handleRefSplit } from '@optimus-web/common';
import { SwipeableEvent } from '@optimus-web/helpers';
import { IconName } from '@optimus-web/icons';
import { BareIconButton } from '../BareIconButton';
import { useIsMobileDevice } from '../hooks';
import { ClassNamed, Identified } from '../types';
import scrollIntoView from '../utils/scrollIntoView';
import { ScrollIndicatorElement, SlideAnimatorSwipeableElement, SlideAnimatorWrapperElement } from './style';

interface ChildrenProps extends Identified {
    ref?: (instance: HTMLDivElement | null) => void | React.RefObject<HTMLDivElement> | null | undefined;
}

export interface SlideAnimatorProps extends ClassNamed {
    selectedChildId?: string;
    scrollAlignment?: ScrollIntoViewOptions['block'];
    scrollBehavior?: ScrollIntoViewOptions['behavior'];
    children: Array<React.ReactElement<ChildrenProps>> | React.ReactElement;

    vertical?: boolean;
    alignToNearestOnSwipeEnd?: boolean;
    initialScrollPosition?: number;
    onSwipe?: (id: string) => void;

    role?: string;

    showArrows?: boolean;
    arrowLeftContainer?: ComponentType;
    arrowRightContainer?: ComponentType;
}

type ScrollDirection = 'backward' | 'forward';

export const SlideAnimator = forwardRef(
    (
        {
            selectedChildId,
            scrollAlignment = 'center',
            scrollBehavior = 'smooth',
            children,
            vertical,
            alignToNearestOnSwipeEnd,
            initialScrollPosition,
            onSwipe,
            showArrows = false,
            arrowLeftContainer: ArrowLeftContainer,
            arrowRightContainer: ArrowRightContainer,
            ...other
        }: SlideAnimatorProps,
        forwardedRef: Ref<HTMLOListElement>
    ) => {
        const isMobile = useIsMobileDevice();

        const animatorWrapperRef = useRef<HTMLOListElement>(null);
        const childrenRefs = useRef<{ [id: string]: HTMLElement | null }>({});
        const previousScrollDelta = useRef(0);

        const [showBackwardArrow, setShowBackwardArrow] = useState(false);
        const [showForwardArrow, setShowForwardArrow] = useState(false);

        const scrollChildWithIdIntoView = useCallback(
            (childId?: string) => {
                if (childId != null && childrenRefs.current[childId]) {
                    scrollIntoView(childrenRefs.current[childId], {
                        behavior: scrollBehavior,
                        block: scrollAlignment,
                        inline: scrollAlignment,
                    });
                }
            },
            [scrollAlignment, scrollBehavior]
        );

        const sizeProperty = vertical ? 'clientHeight' : 'clientWidth';
        const offsetProperty = vertical ? 'offsetTop' : 'offsetLeft';
        const scrollProperty = vertical ? 'scrollTop' : 'scrollLeft';
        const scrollDeltaProperty = vertical ? 'deltaY' : 'deltaX';
        const scrollSizeProperty = vertical ? 'scrollHeight' : 'scrollWidth';

        const setScrollArrows = useCallback(() => {
            window.setTimeout(() => {
                const { current: animatorWrapper } = animatorWrapperRef;

                if (animatorWrapper) {
                    setShowBackwardArrow(animatorWrapper[scrollProperty] > 0);

                    setShowForwardArrow(
                        animatorWrapper[scrollProperty] + animatorWrapper[sizeProperty] !==
                            animatorWrapper[scrollSizeProperty]
                    );
                }
            }, 500);
        }, [scrollProperty, scrollSizeProperty, sizeProperty]);

        useEffect(() => {
            scrollChildWithIdIntoView(selectedChildId);
            setScrollArrows();
        }, [selectedChildId, scrollChildWithIdIntoView, setScrollArrows]);

        useEffect(() => {
            const { current: animatorWrapper } = animatorWrapperRef;

            if (animatorWrapper) {
                setScrollArrows();
            }
        }, [scrollProperty, scrollSizeProperty, setScrollArrows, sizeProperty]);

        const handleChildRef = (childId: string) => (child: HTMLElement | null) => {
            childrenRefs.current[childId] = child;
            setScrollArrows();
        };

        const handleSwiping = (e: SwipeableEvent) => {
            const { current: animatorWrapper } = animatorWrapperRef;
            if (animatorWrapper) {
                const maxScrollDelta = animatorWrapper[scrollSizeProperty] - animatorWrapper[sizeProperty];
                const scrollDelta = e[scrollDeltaProperty];

                // This should be used to create bounce effect
                if (scrollDelta <= maxScrollDelta) {
                    animatorWrapper[scrollProperty] += scrollDelta - previousScrollDelta.current;
                    previousScrollDelta.current = scrollDelta;
                }
            }
        };

        const handleSwiped = () => {
            previousScrollDelta.current = 0;
            const { current: animatorWrapper } = animatorWrapperRef;

            if (animatorWrapper) {
                let scrollThreshold = animatorWrapper[scrollProperty];
                if (scrollThreshold && scrollAlignment === 'center') {
                    scrollThreshold += Math.round(animatorWrapper[sizeProperty] / 2);
                } else if (scrollAlignment === 'end') {
                    scrollThreshold += animatorWrapper[sizeProperty];
                }

                const animatorOffset = animatorWrapper[offsetProperty];
                const newSelectedChildId = Object.keys(childrenRefs.current).find(
                    (childId) =>
                        childrenRefs.current[childId] &&
                        childrenRefs.current[childId][offsetProperty] >= scrollThreshold + animatorOffset
                );

                if (newSelectedChildId) {
                    scrollChildWithIdIntoView(newSelectedChildId);

                    if (onSwipe) {
                        onSwipe(newSelectedChildId);
                    }

                    setScrollArrows();
                }
            }
        };

        const handleArrowClick = (direction: ScrollDirection) => {
            const { current: animatorWrapper } = animatorWrapperRef;

            if (animatorWrapper) {
                let currentScroll = animatorWrapper[scrollProperty];

                animatorWrapper.scrollTo({
                    left:
                        direction === 'backward'
                            ? Math.min((currentScroll -= animatorWrapper[sizeProperty]))
                            : Math.max((currentScroll += animatorWrapper[sizeProperty])),
                    behavior: 'smooth',
                });

                setScrollArrows();
            }
        };

        return (
            <SlideAnimatorSwipeableElement
                trackMouse
                onSwiping={handleSwiping}
                onSwiped={handleSwiped}
                vertical={vertical}
                showArrows={showArrows}
            >
                {showArrows && (
                    <ScrollIndicatorElement as={ArrowLeftContainer} show={showBackwardArrow}>
                        {!isMobile && (
                            <BareIconButton
                                icon={IconName.ChevronLeft}
                                size="small"
                                onClick={() => handleArrowClick('backward')}
                            />
                        )}
                    </ScrollIndicatorElement>
                )}
                <SlideAnimatorWrapperElement
                    ref={handleRefSplit({ ownRef: animatorWrapperRef, forwardedRef })}
                    vertical={vertical}
                    {...other}
                >
                    {Children.map(children, (child) => {
                        const { id } = child.props;
                        return cloneElement(child, {
                            ...child.props,
                            ref: handleChildRef(id),
                        });
                    })}
                </SlideAnimatorWrapperElement>
                {showArrows && (
                    <ScrollIndicatorElement as={ArrowRightContainer} show={showForwardArrow}>
                        {!isMobile && (
                            <BareIconButton
                                icon={IconName.ChevronRight}
                                size="small"
                                onClick={() => handleArrowClick('forward')}
                            />
                        )}
                    </ScrollIndicatorElement>
                )}
            </SlideAnimatorSwipeableElement>
        );
    }
);
